import React, { useState } from 'react';
import { ListChecks, Loader2, Check, Pencil, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { categoriesAPI, productsAPI } from '../services/api';
import { specTemplate, standardKey, CONDITIONS } from '../services/specs';

const ProductSpecsEditor = ({ form, setForm, category, onTemplateSaved }) => {
  const template = specTemplate(category, form.subcategory) || [];
  const attrFields = template.filter((l) => !standardKey(l));
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const scope = form.subcategory || `toute la catégorie ${category?.name || ''}`;

  const openEditor = () => { setText(template.join('\n')); setError(''); setEditing(true); };
  const saveTemplate = async () => {
    setSaving(true); setError('');
    try {
      const updated = await categoriesAPI.setSpecFields(category.id, form.subcategory || '', text.split('\n').map((l) => l.trim()).filter(Boolean));
      onTemplateSaved?.(updated);
      setEditing(false);
    } catch (e) { setError(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };
  const setAttr = (label) => (e) => setForm((f) => ({ ...f, attributes: { ...(f.attributes || {}), [label]: e.target.value } }));

  const [aiBusy, setAiBusy] = useState(false);
  const [aiNotes, setAiNotes] = useState(null);
  const [aiKeys, setAiKeys] = useState([]);
  const [aiUrl, setAiUrl] = useState('');
  const canAi = attrFields.length > 0 && (form.brand || '').trim() && (form.ref || '').trim();
  const fillWithAi = async () => {
    setAiBusy(true); setAiNotes(null);
    try {
      const current = Object.fromEntries(attrFields.map((l) => [l, (form.attributes || {})[l] || '']));
      const r = await productsAPI.assistSpecs({ brand: form.brand, ref: form.ref, name: form.name || '', category: category?.name || '', subcategory: form.subcategory || '', fields: attrFields, current, reference_url: aiUrl.trim() || null });
      const keys = Object.keys(r.values || {});
      if (keys.length) setForm((f) => ({ ...f, attributes: { ...(f.attributes || {}), ...r.values } }));
      setAiKeys(keys);
      setAiNotes({ filled: keys.length, asked: r.asked, confidence: r.confidence, a_verifier: r.a_verifier || [], notes: r.notes || [] });
      keys.length ? toast.success(`${keys.length} caractéristique(s) proposée(s) par l'IA — à vérifier avant publication`) : toast.info("L'IA ne connaît pas ces données avec certitude pour cette référence");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pré-remplissage IA impossible');
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="border-t pt-4" data-testid="product-specs-editor">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><ListChecks size={15} className="text-orange-500" /> Caractéristiques (fiche produit)</h4>
        <div className="flex items-center gap-3">
          {attrFields.length > 0 && !editing && (
            <button type="button" onClick={fillWithAi} disabled={!canAi || aiBusy} title={canAi ? "L'IA propose les valeurs à partir de la marque et de la référence fabricant" : "Renseignez d'abord la marque et la référence fabricant"} className="text-xs font-medium text-violet-700 hover:underline inline-flex items-center gap-1 disabled:opacity-40 disabled:no-underline" data-testid="product-specs-ai-fill">
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Pré-remplir par IA
            </button>
          )}
          {category?.id && !editing && (
            <button type="button" onClick={openEditor} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="product-specs-edit-template">
              <Pencil size={12} /> {template.length ? 'Modifier la liste' : 'Définir la liste'} des caractéristiques ({scope})
            </button>
          )}
        </div>
      </div>
      {attrFields.length > 0 && !editing && (
        <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="product-specs-ai-row">
          <Input value={aiUrl} onChange={(e) => setAiUrl(e.target.value)} placeholder="URL de la fiche fabricant / catalogue (facultatif) — l'IA y relève les valeurs exactes" className="h-8 text-xs flex-1" data-testid="product-specs-ai-url" />
          <span className="text-[11px] text-gray-500 whitespace-nowrap">{canAi ? `Réf. ${form.brand} ${form.ref}` : 'Renseignez marque + référence pour activer l\'IA'}</span>
        </div>
      )}
      {aiNotes && (
        <div className="mb-3 rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2 text-xs text-violet-900" data-testid="product-specs-ai-notes">
          <p><strong>{aiNotes.filled}/{aiNotes.asked}</strong> caractéristique(s) proposée(s) par l'IA (confiance {aiNotes.confidence}). Les champs surlignés sont des propositions : vérifiez-les avec la fiche fabricant avant publication.</p>
          {aiNotes.notes.map((n, i) => <p key={`n${i}`} className="text-violet-700">{n}</p>)}
          {aiNotes.a_verifier.length > 0 && <ul className="list-disc ml-4 mt-1">{aiNotes.a_verifier.map((x, i) => <li key={i}>{x}</li>)}</ul>}
        </div>
      )}
      {editing && (
        <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50/50 p-3 space-y-2" data-testid="product-specs-template-editor">
          <p className="text-xs text-gray-600">Une caractéristique par ligne, dans l'ordre d'affichage. Cette liste s'applique à <strong>{scope}</strong> et à tous ses produits. Les libellés « Numéro d'article », « Notre prix », « Fabricant », « Numéro de EAN », « État » sont remplis automatiquement.</p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder={"Type de filtre\nHauteur [mm]\nDiamètre [mm]\nNuméro d'article\nNotre prix\nFabricant\nNuméro de EAN\nÉtat"} data-testid="product-specs-template-text" />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>Annuler</Button>
            <Button type="button" size="sm" onClick={saveTemplate} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="product-specs-template-save">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />} Enregistrer la liste</Button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Numéro de EAN</label>
          <Input value={form.ean || ''} onChange={(e) => setForm((f) => ({ ...f, ean: e.target.value }))} placeholder="4011558xxxxxx" maxLength={20} data-testid="product-ean-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">État</label>
          <select value={form.condition || 'Neuf'} onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" data-testid="product-condition-select">
            {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {attrFields.map((label) => (
          <div key={label}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
            <Input value={(form.attributes || {})[label] || ''} onChange={setAttr(label)} className={aiKeys.includes(label) ? 'border-violet-400 bg-violet-50/40' : ''} data-testid={`product-attr-${label}`} />
          </div>
        ))}
      </div>
      {attrFields.length === 0 && !editing && (
        <p className="text-xs text-gray-400 mt-2" data-testid="product-specs-empty">Aucune caractéristique spécifique définie pour {scope}{category?.id ? ' — cliquez sur « Définir la liste » pour en ajouter.' : '.'}</p>
      )}
    </div>
  );
};

export default ProductSpecsEditor;
